import React from 'react';
import { ApprovalStatusHistory, getCreditApplicationTypeLabel } from '@dill/dill-shared';
import moment from 'moment';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';
import AppCreditApplicationStatus from '../CreditApplications/CreditApplicationStatus';

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{label}</p>
    <div className="text-sm text-gray-800">{children}</div>
  </div>
);

const CreditAppHistoryDetailsModal = ({
  open,
  handleClose,
  approvalStatusHistory,
}: {
  open: boolean;
  handleClose: () => void;
  approvalStatusHistory: ApprovalStatusHistory;
}) => {
  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Application History Details"
      maxWidth="600px"
      footer={<DillButton label="Close" variant="secondary" onClick={handleClose} />}>
      <div className="flex flex-col gap-4">
        {/* Summary card */}
        <div className="grid grid-cols-3 gap-4 bg-gray-50 border border-gray-100 rounded-xl p-4">
          <Field label="Status">
            <AppCreditApplicationStatus
              status={approvalStatusHistory.status}
              wrapperClassName="w-fit text-nowrap"
            />
          </Field>
          <Field label="Date">
            {moment(approvalStatusHistory.date).format('MM/DD/YYYY')}
            <span className="block text-xs text-gray-400">
              {moment(approvalStatusHistory.date).format('LT')}
            </span>
          </Field>
          <Field label="Application Type">
            <span className="capitalize">
              {getCreditApplicationTypeLabel(approvalStatusHistory.applicationType, { long: true })}
            </span>
          </Field>
        </div>

        {/* Action row */}
        <div className="grid grid-cols-2 gap-4 px-1">
          <Field label="Action">
            <span className="capitalize">{approvalStatusHistory.action}</span>
          </Field>
          <Field label="Action By">{approvalStatusHistory.actionBy}</Field>
        </div>

        {/* Comment & Notes */}
        {(approvalStatusHistory.comment || approvalStatusHistory.notes) && (
          <div className="flex flex-col gap-3">
            {approvalStatusHistory.comment && (
              <div className="flex flex-col gap-1.5 bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-[10px] font-semibold uppercase text-gray-400 tracking-widest">
                  Comment
                </p>
                {/<[a-z][\s\S]*>/i.test(approvalStatusHistory.comment) ? (
                  <div
                    className="text-sm text-gray-800 [&_a]:text-blue-600 [&_a]:underline"
                    dangerouslySetInnerHTML={{ __html: approvalStatusHistory.comment }}
                  />
                ) : (
                  <p className="text-sm text-gray-800">{approvalStatusHistory.comment}</p>
                )}
              </div>
            )}
            {approvalStatusHistory.notes && (
              <div className="flex flex-col gap-1.5 bg-gray-50 border border-gray-100 rounded-xl p-4">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                  Notes
                </p>
                <p className="text-sm text-gray-800">{approvalStatusHistory.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Fields */}
        {approvalStatusHistory?.fields && approvalStatusHistory.fields.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest px-1">
              Fields
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {approvalStatusHistory.fields.map((field, indx) => (
                <div
                  key={indx + 'field'}
                  className="flex flex-col gap-0.5 bg-gray-50 border border-gray-100 rounded-xl p-3">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                    Field Name
                  </p>
                  <p className="text-sm text-gray-800 truncate">{field.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DillModal>
  );
};

export default CreditAppHistoryDetailsModal;
